import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { create, keyResolver, windowScheduler } from "@yornaath/batshit";
import type { NewPurchaseBody, Purchase } from "shared";
import { Api } from "@/api.config";
import { QueryKeys } from "@/LinkGen";

const batcher = create({
  fetcher: async (IDs: number[]) => {
    return Api.Purchase(IDs);
  },
  resolver: keyResolver("ID"),
  scheduler: windowScheduler(50),
});

const _queryOptions = (ID: number) =>
  queryOptions({
    queryKey: QueryKeys.Purchase(ID),
    queryFn: () => batcher.fetch(ID),
  });

export function usePurchaseList() {
  const { data } = useSuspenseQuery({
    queryKey: QueryKeys.Purchase.List(),
    queryFn: () => Api.PurchaseList().then((res) => res.data),
  });

  return data;
}

export function usePurchase(ID: number) {
  const { data } = useSuspenseQuery(_queryOptions(ID));

  return data;
}

export function usePurchasesSuspense(IDs: number[]): Purchase[] {
  return useSuspenseQueries({
    queries: IDs.map((ID) => _queryOptions(ID)),
    combine: (results) => results.map((x) => x.data),
  });
}

export function usePurchaseActions() {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (purchaseID: number) => {
      await Api.PurchaseDelete(purchaseID);

      await queryClient.refetchQueries({
        queryKey: QueryKeys.Purchase.List(),
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: NewPurchaseBody) => {
      const { data } = await Api.PurchaseNew(body);

      await queryClient.invalidateQueries({
        queryKey: QueryKeys.Purchase.List(),
      });

      return data;
    },
  });

  return {
    delete: deleteMutation.mutateAsync,
    create: createMutation.mutateAsync,
  };
}
